import { T } from '@tolgee/react';
import { Divider, Skeleton } from 'antd';
import { useState } from 'react';
import styled from 'styled-components';

import { CrossSvg } from '../../../assets/icons';
import theme from '../../../assets/theme';
import { MaterialAttachment } from '../../../hooks/useUploadMaterialAttachment';
import { IconWrapper } from '../../_atoms/IconWrapper';
import { LoadingDots } from '../../_atoms/StreamingLongAnswerList/StreamingLongAnswerList';
import GameImageRenderer from '../../_molecules/GameImageRenderer';
import {
  OtherFileRenderer,
  TextFileRenderer,
} from '../../_molecules/GameImageRenderer/GameImageRenderer';
import { MaterialAttachmentPreview } from './MaterialAttachmentPreview';

export function MaterialAttachments({
  attachments,
  removeAttachment,
  isUploading,
}: {
  attachments: Array<MaterialAttachment>;
  removeAttachment: (fileId: string) => void;
  isUploading: boolean;
}) {
  const [isAttachmentPreviewOpen, setIsAttachmentPreviewOpen] = useState(false);
  const [selectedAttachment, setSelectedAttachment] = useState<MaterialAttachment>();
  return (
    <AttachmentsWrapper>
      <MaterialAttachmentPreview
        isAttachmentPreviewOpen={isAttachmentPreviewOpen}
        setIsAttachmentPreviewOpen={setIsAttachmentPreviewOpen}
        selectedAttachment={selectedAttachment}
      />
      <Divider style={{ margin: '0px' }} />
      <InputAttachmentsGrid>
        {attachments.map(file => {
          const { unprocessedAttachment, ...attachment } = file;

          // we may have processed their docx into markdown, but we still want to display the attachment as docx
          // so we need to display the unprocessed attachment if it exists
          const displayAttachment = unprocessedAttachment ?? attachment;

          return (
            <InputAttachmentContainer
              key={attachment.fileId}
              onClick={() => {
                setSelectedAttachment(displayAttachment);
                setIsAttachmentPreviewOpen(true);
              }}
            >
              <AttachmentCloseButton
                className="attachment-close-button"
                onClick={e => {
                  e.stopPropagation(); // Prevent triggering the container click
                  removeAttachment(attachment.fileId);
                }}
              >
                <IconWrapper height="12px" width="12px" color="white">
                  <CrossSvg />
                </IconWrapper>
              </AttachmentCloseButton>
              {displayAttachment.mimetype.startsWith('image/') && (
                <AttachmentPreview>
                  <GameImageRenderer
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                    }}
                    fileId={displayAttachment.fileId}
                    fileMimetype={displayAttachment.mimetype}
                    fileAltText={''}
                    isDiscoverTemplate={false}
                    isAuthenticated
                    isOwner // it should be impossible to not be owner and authenticated
                  />
                </AttachmentPreview>
              )}
              {displayAttachment.mimetype === 'text/plain' &&
                displayAttachment.attachmentMethod === 'paste' && (
                  <AttachmentPreview>
                    <TextFileRenderer fileId={displayAttachment.fileId} />
                    <TextFileBadge>
                      <BadgeText>
                        <T keyName="Pasted" defaultValue="Pasted" />
                      </BadgeText>
                    </TextFileBadge>
                  </AttachmentPreview>
                )}
              {displayAttachment.mimetype === 'text/plain' &&
                displayAttachment.attachmentMethod === 'upload' && (
                  <AttachmentPreview>
                    <TextFileRenderer fileId={displayAttachment.fileId} />
                  </AttachmentPreview>
                )}
              {displayAttachment.attachmentMethod === 'upload' &&
                displayAttachment.mimetype !== 'text/plain' &&
                !displayAttachment.mimetype.startsWith('image') && (
                  <AttachmentPreview>
                    <OtherFileRenderer
                      fileId={displayAttachment.fileName}
                      fileMimetype={displayAttachment.mimetype}
                    />
                  </AttachmentPreview>
                )}
            </InputAttachmentContainer>
          );
        })}
        {isUploading && (
          <InputAttachmentContainer>
            <AttachmentPreview>
              <StyledSkeleton active={true} />

              <TextFileBadge>
                <BadgeText>
                  {<T keyName="Uploading" defaultValue="Uploading" />}
                  <LoadingDots>...</LoadingDots>
                </BadgeText>
              </TextFileBadge>
            </AttachmentPreview>
          </InputAttachmentContainer>
        )}
      </InputAttachmentsGrid>
    </AttachmentsWrapper>
  );
}

// Input Attachment Components
const AttachmentsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  background-color: #fff9f3;
  align-items: center;
  justify-content: center;
  width: calc(100% + 22px);
  margin: 10px -11px -11px -11px;
  padding-top: 0px;
  border-radius: 0px 0px 10px 10px;
`;

const InputAttachmentsGrid = styled.div`
  display: grid;
  width: 100%;
  gap: 10px;
  height: 110px;
  grid-template-columns: repeat(auto-fill, minmax(90px, 1fr));
  grid-template-rows: 90px;
  position: relative;
  padding: 10px 10px 10px 10px;
`;

const InputAttachmentContainer = styled.div`
  width: 100%;
  height: 100%;
  position: relative;
  cursor: pointer;

  &:hover {
    .attachment-close-button {
      opacity: 1;
      visibility: visible;
    }
  }
`;

const AttachmentCloseButton = styled.div`
  position: absolute;
  z-index: 1;
  top: 0;
  left: 0;
  transform: translate(-25%, -25%);
  height: 16px;
  width: 16px;
  border: 1px solid ${theme.colors.grey};
  border-radius: 50px;
  display: grid;
  align-content: center;
  background: ${theme.colors.greyDark};
  justify-content: center;
  cursor: pointer;
  opacity: 0;
  visibility: hidden;
  transition: opacity 0.2s ease, visibility 0.2s ease;

  svg {
    height: 8px;
    width: 8px;
  }
`;

export const AttachmentPreview = styled.div`
  width: 100%;
  height: 100%;
  overflow: hidden;
  border: 1px solid ${theme.colors.grey};
  border-radius: 5px;
  position: relative;
`;

export const TextFileBadge = styled.div`
  position: absolute;
  bottom: 5px;
  left: 5px;
  height: 20px;
  border: 1px solid ${theme.colors.greyDark};
  border-radius: 5px;
  padding: 4px 4px 2px 4px;
  display: grid;
  align-content: center;
  background: white;
  box-shadow: 0px 0px 1px 1px rgba(0, 0, 0, 0.1);
`;

export const BadgeText = styled.span`
  text-transform: uppercase;
  font-size: 12px;
`;

const StyledSkeleton = styled(Skeleton.Button)`
  width: 100% !important;
  height: 100% !important;
`;
